// Güvenlik başlıkları: içerik güvenlik politikası (CSP, nonce + 'strict-dynamic'), Trusted Types, HSTS ve diğerleri.
// Saf modül (sunucuya özgü bağımlılık yok): next.config.ts, src/proxy.ts ve birim testleri (tests/unit/security-headers.test.ts)
// aynı değerleri kullanır. Kararlar ve ödünleşimler: docs/security-headers.md

/**
 * Google Analytics 4 (yalnızca ziyaretçi onay verince yüklenir; docs/analytics.md). gtag.js betiği nonce'lu bir
 * betiğin eklediği <script> olduğu için 'strict-dynamic' ile çalışır (betik kaynağında alan adı listesi CSP3
 * tarayıcılarında yok sayılır); ölçüm istekleri ve yedek piksel için alan adları gerekir.
 */
export const GA_SCRIPT_ORIGIN = "https://www.googletagmanager.com";
const GA_CONNECT = ["https://*.google-analytics.com", "https://*.analytics.google.com", "https://*.googletagmanager.com", "https://www.google.com"];
const GA_IMG = ["https://*.google-analytics.com", "https://*.googletagmanager.com", "https://www.google.com"];

/** Sayfa metnine gömülebilen tek çerçeve: YouTube çerezsiz oynatıcısı (src/lib/utils/html.ts yalnızca bu adresi bırakır) */
const FRAME_SRC = ["https://www.youtube-nocookie.com"];

export type CspOptions = {
  /** İstek başına üretilen nonce (base64) */
  nonce: string;
  /** Geliştirme sunucusu: React'in hata ayıklama için eval kullanması ('unsafe-eval'); Trusted Types yalnızca raporlanır */
  dev?: boolean;
  /** İstek HTTPS ile geldi (ters vekilin X-Forwarded-Proto'su): upgrade-insecure-requests yalnızca o zaman */
  https?: boolean;
};

/** Trusted Types: zorunlu kılan yönerge ve izin verilen ilke adları (default: src/lib/security/trusted-types.ts) */
const TRUSTED_TYPES = ["require-trusted-types-for 'script'", "trusted-types default 'allow-duplicates'"];

function directives(list: [string, string[]][]): string {
  return list.map(([name, values]) => (values.length ? `${name} ${values.join(" ")}` : name)).join("; ");
}

/**
 * Sayfa (HTML) yanıtlarının CSP'si. Katı CSP (strict CSP, nonce tabanlı):
 *  - script-src: yalnızca bu isteğin nonce'unu taşıyan betikler ve onların eklediği betikler ('strict-dynamic').
 *    'unsafe-inline' ve https: yalnızca nonce/'strict-dynamic' bilmeyen çok eski tarayıcılar için geri dönüş;
 *    CSP3 tarayıcıları nonce varken ikisini de yok sayar.
 *  - style-src 'unsafe-inline': React/next/image satır içi style özniteliği yazar (nonce öznitelikleri kapsamaz).
 *  - object-src 'none', base-uri 'none', frame-ancestors 'none' (tıklama korsanlığı), form-action 'self'.
 *  - Trusted Types (require-trusted-types-for 'script'): üretimde zorunlu; geliştirmede ayrı Report-Only başlığında.
 */
export function contentSecurityPolicy({ nonce, dev = false, https = false }: CspOptions): string {
  return directives([
    ["default-src", ["'self'"]],
    ["script-src", [`'nonce-${nonce}'`, "'strict-dynamic'", "https:", "'unsafe-inline'", ...(dev ? ["'unsafe-eval'"] : [])]],
    ["style-src", ["'self'", "'unsafe-inline'"]],
    ["img-src", ["'self'", "data:", "blob:", ...GA_IMG]],
    ["font-src", ["'self'"]],
    ["connect-src", ["'self'", ...GA_CONNECT]],
    ["frame-src", FRAME_SRC],
    ["object-src", ["'none'"]],
    ["base-uri", ["'none'"]],
    ["form-action", ["'self'"]],
    ["frame-ancestors", ["'none'"]],
    ...(dev ? [] : TRUSTED_TYPES.map((d) => [d, []] as [string, string[]])),
    ...(https ? [["upgrade-insecure-requests", []] as [string, string[]]] : []),
  ]);
}

/** Geliştirme sunucusunda Trusted Types yalnızca raporlanır (Turbopack HMR ve hata katmanı denetlenmedi) */
export function devTrustedTypesReportOnly(): string {
  return TRUSTED_TYPES.join("; ");
}

/** Sayfa dışı yanıtlar (API, /medya): hiçbir şey yüklemez, çerçevelenmez */
export const NON_DOCUMENT_CSP = directives([
  ["default-src", ["'none'"]],
  ["frame-ancestors", ["'none'"]],
  ["base-uri", ["'none'"]],
  ["form-action", ["'none'"]],
]);

/** İstek başına nonce: 128 bit rastgele, base64 (Next.js CSP kılavuzu) */
export function createNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/**
 * HSTS: 2 yıl + alt alan adları. `preload` yalnızca HSTS_PRELOAD=1 ile (önce flyexpress.com'un TÜM alt alan adları
 * HTTPS sunmalı; preload listesine girmek kolay, çıkmak aylar sürer). Yalnızca HTTPS isteklerine eklenir (next.config.ts).
 */
export function hstsValue(preload: boolean): string {
  return `max-age=63072000; includeSubDomains${preload ? "; preload" : ""}`;
}

/** Her yanıtta (next.config.ts → headers) */
export const COMMON_SECURITY_HEADERS: { key: string; value: string }[] = [
  { key: "X-Content-Type-Options", value: "nosniff" },
  { key: "Referrer-Policy", value: "strict-origin-when-cross-origin" },
  // Eski tarayıcılar için; CSP frame-ancestors 'none' ile aynı
  { key: "X-Frame-Options", value: "DENY" },
  // Başka kökenden açılan pencereyle (window.opener) bağ yok. Paylaşım bağlantıları <a target="_blank" rel="noopener">,
  // navigator.share ve panoya kopyalama etkilenmez (window.open kullanılmıyor).
  { key: "Cross-Origin-Opener-Policy", value: "same-origin" },
  // Sitenin kullanmadığı güçlü özellikler kapalı (web-share ve clipboard-write açık kalır: paylaşım düğmeleri)
  {
    key: "Permissions-Policy",
    value: "accelerometer=(), browsing-topics=(), camera=(), display-capture=(), geolocation=(), gyroscope=(), hid=(), magnetometer=(), microphone=(), midi=(), payment=(), serial=(), usb=()",
  },
];
